import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:    '#0A1628', gold:    '#C9A84C', black:   '#0A0A0A',
  white:   '#FFFFFF', surface: '#F5F6F8', border:  '#D0D5DD',
  borderLt:'#E8EBF0', text:    '#0A0A0A', textSec: '#3D4552',
  muted:   '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger:  '#C0222A', amber:   '#92640A',
  teal:    '#0D6E8A',
};

const MIN_PEERS = 3;

interface Fund {
  id: string; name: string; asset_class: string; strategy: string;
  management_fee: number; performance_fee: number; minimum_subscription: number;
  aum_min: number; aum_max: number; inception_date: string;
}
interface PeerFund { id: string; management_fee: number; performance_fee: number; minimum_subscription: number; aum_min: number; aum_max: number; }
interface FundStats { annReturn: number; vol: number; sharpe: number; sortino: number; maxDD: number; bestMonth: number; worstMonth: number; avgMonthly: number; months: number; }
interface PeerStats {
  count: number;
  annReturn: { avg: number; min: number; max: number };
  vol:       { avg: number; min: number; max: number };
  sharpe:    { avg: number; min: number; max: number };
  sortino:   { avg: number; min: number; max: number };
  maxDD:     { avg: number; min: number; max: number };
  mgmtFee:   { avg: number; min: number; max: number };
  perfFee:   { avg: number; min: number; max: number };
  minSub:    { avg: number; min: number; max: number };
  aum:       { avg: number; min: number; max: number };
}

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  re: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};

const aumRanges = [
  { label: 'All sizes',       min: 0,    max: 999999 },
  { label: 'Under $100mm',    min: 0,    max: 100    },
  { label: '$100mm – $500mm', min: 100,  max: 500    },
  { label: '$500mm – $1bn',   min: 500,  max: 1000   },
  { label: '$1bn – $5bn',     min: 1000, max: 5000   },
  { label: '$5bn+',           min: 5000, max: 999999 },
];

function computeStats(returns: number[]): FundStats | null {
  if (returns.length < 6) return null;
  const mean = returns.reduce((a, b) => a + b, 0) / returns.length;
  const annReturn = mean * 12;
  const variance = returns.reduce((s, r) => s + Math.pow(r - mean, 2), 0) / (returns.length - 1);
  const vol = Math.sqrt(variance) * Math.sqrt(12);
  const sharpe = vol === 0 ? 0 : (annReturn - 5) / vol;
  const downside = returns.filter(r => r < 0);
  const downsideVol = downside.length > 0 ? Math.sqrt(downside.reduce((s, r) => s + Math.pow(r, 2), 0) / downside.length) * Math.sqrt(12) : 0;
  const sortino = downsideVol === 0 ? 0 : (annReturn - 5) / downsideVol;
  let peak = 100, nav = 100, maxDD = 0;
  for (const r of returns) {
    nav = nav * (1 + r / 100);
    if (nav > peak) peak = nav;
    const dd = (nav - peak) / peak * 100;
    if (dd < maxDD) maxDD = dd;
  }
  return { annReturn, vol, sharpe, sortino, maxDD, bestMonth: Math.max(...returns), worstMonth: Math.min(...returns), avgMonthly: mean, months: returns.length };
}

function getQuartile(value: number, min: number, max: number, lowerIsBetter = false): { label: string; color: string } {
  const range = max - min;
  if (range === 0) return { label: 'Average', color: C.muted };
  const pct = (value - min) / range;
  if (lowerIsBetter) {
    if (pct <= 0.25) return { label: 'Top quartile',    color: C.success };
    if (pct <= 0.5)  return { label: 'Above average',   color: C.teal    };
    if (pct <= 0.75) return { label: 'Below average',   color: C.amber   };
    return                  { label: 'Bottom quartile', color: C.danger  };
  } else {
    if (pct >= 0.75) return { label: 'Top quartile',    color: C.success };
    if (pct >= 0.5)  return { label: 'Above average',   color: C.teal    };
    if (pct >= 0.25) return { label: 'Below average',   color: C.amber   };
    return                  { label: 'Bottom quartile', color: C.danger  };
  }
}

function Bar({ value, min, max, color }: { value: number; min: number; max: number; color: string }) {
  const range = max - min;
  const pct = range === 0 ? 50 : ((value - min) / range) * 100;
  return (
    <div style={{ position: 'relative', height: '5px', background: C.borderLt, borderRadius: '2px', margin: '8px 0 4px' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.max(1, pct)}%`, background: color, borderRadius: '2px', transition: 'width 0.3s' }} />
      <div style={{ position: 'absolute', top: '-4px', left: `${Math.max(0, pct - 0.5)}%`, width: '2px', height: '13px', background: C.navy, borderRadius: '1px' }} />
    </div>
  );
}

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', padding: '18px 20px', marginBottom: '10px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

export default function PeerAnalysis() {
  const { fundId } = useParams();
  const navigate = useNavigate();
  const { firm } = useProfile();
  const [fund, setFund]           = useState<Fund | null>(null);
  const [myStats, setMyStats]     = useState<FundStats | null>(null);
  const [peerStats, setPeerStats] = useState<PeerStats | null>(null);
  const [peerCount, setPeerCount] = useState(0);
  const [loading, setLoading]     = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState<'performance' | 'fees'>('performance');
  const [allPeerFunds, setAllPeerFunds] = useState<(PeerFund & { strategy: string; asset_class: string })[]>([]);
  const [availableStrategies, setAvailableStrategies] = useState<string[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [selectedAumRange, setSelectedAumRange] = useState(0);
  const [autoGroupLabel, setAutoGroupLabel] = useState('');

  useEffect(() => { if (fundId) fetchData(); }, [fundId]);
  useEffect(() => { if (allPeerFunds.length > 0 && fund) computePeerStats(); }, [selectedStrategy, selectedAumRange, allPeerFunds, fund]);

  const fetchData = async () => {
    const { data: fundData } = await supabase.from('funds').select('*').eq('id', fundId).single();
    if (!fundData) { setLoading(false); return; }
    setFund(fundData);
    const { data: myPerf } = await supabase.from('fund_performance').select('return_pct').eq('fund_id', fundId).order('year', { ascending: true }).order('month', { ascending: true });
    setMyStats(computeStats((myPerf || []).map(p => p.return_pct)));
    const { data: peers } = await supabase.from('funds')
      .select('id, management_fee, performance_fee, minimum_subscription, aum_min, aum_max, strategy, asset_class')
      .eq('asset_class', fundData.asset_class).eq('status', 'open').neq('id', fundId);
    const peerList = peers || [];
    setAllPeerFunds(peerList);
    const strategies = [...new Set(peerList.map(f => f.strategy).filter(Boolean))];
    setAvailableStrategies(strategies);
    const strategyPeers = peerList.filter(f => f.strategy === fundData.strategy);
    if (strategyPeers.length >= MIN_PEERS) {
      setSelectedStrategy(fundData.strategy);
      setAutoGroupLabel(`${fundData.strategy} funds (auto-selected)`);
    } else {
      setSelectedStrategy('');
      setAutoGroupLabel(`All ${classLabels[fundData.asset_class] || fundData.asset_class} funds (auto-selected)`);
    }
    setLoading(false);
  };

  const computePeerStats = async () => {
    if (!fund || allPeerFunds.length === 0) return;
    setCalculating(true);
    const aumRange = aumRanges[selectedAumRange];
    const filtered = allPeerFunds.filter(f => {
      const aumMid = (f.aum_min + f.aum_max) / 2;
      return (!selectedStrategy || f.strategy === selectedStrategy) && aumMid >= aumRange.min && aumMid <= aumRange.max;
    });
    setPeerCount(filtered.length);
    if (filtered.length < MIN_PEERS) { setPeerStats(null); setCalculating(false); return; }
    const { data: peerPerf } = await supabase.from('fund_performance').select('fund_id, return_pct').in('fund_id', filtered.map(f => f.id));
    const perfByFund: Record<string, number[]> = {};
    (peerPerf || []).forEach(p => { if (!perfByFund[p.fund_id]) perfByFund[p.fund_id] = []; perfByFund[p.fund_id].push(p.return_pct); });
    const peerStatsList = filtered.map(f => computeStats(perfByFund[f.id] || [])).filter(Boolean) as FundStats[];
    if (peerStatsList.length < MIN_PEERS) { setPeerStats(null); setCalculating(false); return; }
    const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
    const agg = (key: keyof FundStats) => { const v = peerStatsList.map(s => s[key] as number); return { avg: avg(v), min: Math.min(...v), max: Math.max(...v) }; };
    const feeAgg = (key: keyof PeerFund) => { const v = filtered.map(f => f[key] as number).filter(Boolean); return { avg: avg(v), min: Math.min(...v), max: Math.max(...v) }; };
    setPeerStats({
      count: peerStatsList.length, annReturn: agg('annReturn'), vol: agg('vol'),
      sharpe: agg('sharpe'), sortino: agg('sortino'), maxDD: agg('maxDD'),
      mgmtFee: feeAgg('management_fee'), perfFee: feeAgg('performance_fee'),
      minSub: feeAgg('minimum_subscription'),
      aum: { avg: avg(filtered.map(f => (f.aum_min + f.aum_max) / 2)), min: Math.min(...filtered.map(f => f.aum_min)), max: Math.max(...filtered.map(f => f.aum_max)) },
    });
    setCalculating(false);
  };

  if (loading) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading peer analysis...</div>
    </div>
  );
  if (!fund) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh' }}>
      <div style={{ color: C.danger, fontSize: '13px', fontFamily: 'system-ui' }}>Fund not found.</div>
    </div>
  );

  const inputStyle: React.CSSProperties = {
    background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
    color: C.text, fontSize: '12.5px', padding: '7px 10px',
    cursor: 'pointer', fontFamily: 'system-ui', outline: 'none',
  };

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <button onClick={() => navigate('/peer-analysis')} style={{
        background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
        color: C.textSec, padding: '5px 12px', cursor: 'pointer',
        fontSize: '12px', marginBottom: '20px', fontFamily: 'system-ui', fontWeight: 500,
      }}>← Back</button>

      <div style={{ marginBottom: '20px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
          Peer Analysis
        </h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>
          {fund.name} · {fund.strategy || classLabels[fund.asset_class]}
        </p>
      </div>

      {/* ── Filters ── */}
      <div style={{ ...card, marginBottom: '14px' }}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
          Peer Group Filters
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '5px' }}>Strategy</div>
            <select style={inputStyle} value={selectedStrategy} onChange={e => setSelectedStrategy(e.target.value)}>
              <option value="">All {classLabels[fund.asset_class]} strategies</option>
              {availableStrategies.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '5px' }}>AUM Range</div>
            <select style={inputStyle} value={selectedAumRange} onChange={e => setSelectedAumRange(Number(e.target.value))}>
              {aumRanges.map((r, i) => <option key={i} value={i}>{r.label}</option>)}
            </select>
          </div>
          <button onClick={() => {
            const sp = allPeerFunds.filter(f => f.strategy === fund.strategy);
            setSelectedStrategy(sp.length >= MIN_PEERS ? fund.strategy : '');
            setSelectedAumRange(0);
          }} style={{ ...inputStyle, color: C.textSec, cursor: 'pointer' }}>
            Reset to Auto
          </button>
        </div>
        {autoGroupLabel && (
          <div style={{ fontSize: '11px', color: C.teal, marginTop: '10px', fontWeight: 500 }}>
            ✦ {autoGroupLabel}
          </div>
        )}
      </div>

      {/* ── Peer count badge ── */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', borderLeft: `3px solid ${peerCount >= MIN_PEERS ? C.success : C.danger}` }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: C.text, marginBottom: '3px' }}>
            {selectedStrategy || `All ${classLabels[fund.asset_class] || fund.asset_class}`} funds
          </div>
          <div style={{ fontSize: '12px', color: C.muted }}>
            {peerCount >= MIN_PEERS
              ? `${peerCount} anonymous funds · All data aggregated, no individual details shown`
              : `Only ${peerCount} peer fund${peerCount !== 1 ? 's' : ''}, minimum ${MIN_PEERS} required`}
          </div>
        </div>
        <div style={{
          padding: '4px 12px', borderRadius: '3px', fontSize: '11px', fontWeight: 700,
          letterSpacing: '0.4px', textTransform: 'uppercase',
          background: peerCount >= MIN_PEERS ? '#F0FDF4' : '#FEF2F2',
          color: peerCount >= MIN_PEERS ? C.success : C.danger,
          border: `1px solid ${peerCount >= MIN_PEERS ? '#BBF7D0' : '#FECACA'}`,
        }}>
          {peerCount >= MIN_PEERS ? `${peerCount} peers` : 'Insufficient data'}
        </div>
      </div>

      {calculating ? (
        <div style={{ textAlign: 'center', padding: '40px', color: C.muted, fontSize: '13px' }}>Calculating...</div>
      ) : peerCount < MIN_PEERS || !peerStats ? (
        <div style={{ ...card, textAlign: 'center', padding: '60px' }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '18px', color: C.text, fontWeight: 400, marginBottom: '8px' }}>Not enough peer data</div>
          <div style={{ fontSize: '13px', color: C.muted, maxWidth: '360px', margin: '0 auto', lineHeight: 1.6 }}>
            Try broadening your filters, select "All strategies" or "All sizes" to include more peers.
          </div>
        </div>
      ) : (
        <>
          {/* ── Tabs ── */}
          <div style={{ display: 'flex', gap: '0', marginBottom: '16px', border: `1px solid ${C.border}`, borderRadius: '3px', width: 'fit-content', overflow: 'hidden' }}>
            {(['performance', 'fees'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{
                padding: '7px 20px', fontSize: '12px', cursor: 'pointer', border: 'none',
                background: activeTab === tab ? C.navy : C.white,
                color: activeTab === tab ? C.white : C.textSec,
                fontFamily: 'system-ui', fontWeight: 600,
                textTransform: 'capitalize', letterSpacing: '0.3px',
                borderRight: tab === 'performance' ? `1px solid ${C.border}` : 'none',
              }}>{tab}</button>
            ))}
          </div>

          {activeTab === 'performance' && (
            <>
              {!myStats ? (
                <div style={card}>
                  <div style={{ fontSize: '13px', color: C.muted }}>No performance data uploaded yet. Upload at least 6 months of returns to see peer comparison.</div>
                </div>
              ) : (
                <>
                  {[
                    { label: 'Annualised Return',    myVal: myStats.annReturn, peer: peerStats.annReturn, format: (v: number) => `${v.toFixed(2)}%`,  lowerIsBetter: false },
                    { label: 'Annualised Volatility', myVal: myStats.vol,       peer: peerStats.vol,       format: (v: number) => `${v.toFixed(2)}%`,  lowerIsBetter: true  },
                    { label: 'Sharpe Ratio',          myVal: myStats.sharpe,    peer: peerStats.sharpe,    format: (v: number) => v.toFixed(2),         lowerIsBetter: false },
                    { label: 'Sortino Ratio',         myVal: myStats.sortino,   peer: peerStats.sortino,   format: (v: number) => v.toFixed(2),         lowerIsBetter: false },
                    { label: 'Max Drawdown',          myVal: myStats.maxDD,     peer: peerStats.maxDD,     format: (v: number) => `${v.toFixed(2)}%`,  lowerIsBetter: true  },
                    { label: 'Best Month',            myVal: myStats.bestMonth, peer: peerStats.annReturn, format: (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(2)}%`, lowerIsBetter: false },
                    { label: 'Worst Month',           myVal: myStats.worstMonth,peer: peerStats.annReturn, format: (v: number) => `${v.toFixed(2)}%`,  lowerIsBetter: true  },
                  ].map((row, i) => {
                    const q = getQuartile(row.myVal, row.peer.min, row.peer.max, row.lowerIsBetter);
                    const diff = row.myVal - row.peer.avg;
                    return (
                      <div key={i} style={card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                          <div>
                            <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '4px' }}>{row.label}</div>
                            <div style={{ fontSize: '24px', fontFamily: 'Georgia, serif', color: C.navy, fontWeight: 400 }}>{row.format(row.myVal)}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 10px', borderRadius: '2px', background: q.color + '15', color: q.color, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                              {q.label}
                            </span>
                            <div style={{ fontSize: '11px', color: C.muted, marginTop: '6px' }}>
                              Peer avg: <span style={{ fontFamily: '"Courier New", monospace', fontWeight: 600 }}>{row.format(row.peer.avg)}</span>
                              <span style={{ color: diff >= 0 ? C.success : C.danger, marginLeft: '6px', fontFamily: '"Courier New", monospace', fontWeight: 600 }}>
                                ({diff >= 0 ? '+' : ''}{row.format(diff)})
                              </span>
                            </div>
                          </div>
                        </div>
                        <Bar value={row.myVal} min={row.peer.min} max={row.peer.max} color={q.color} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: C.mutedLt, marginTop: '2px', fontFamily: '"Courier New", monospace' }}>
                          <span>Min: {row.format(row.peer.min)}</span>
                          <span>Max: {row.format(row.peer.max)}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ fontSize: '11px', color: C.mutedLt, textAlign: 'center', marginTop: '4px', letterSpacing: '0.2px' }}>
                    Based on {peerStats.count} anonymous peer funds · All peer data aggregated only
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === 'fees' && (
            <>
              {[
                { label: 'Management Fee',        myVal: fund.management_fee,          peer: peerStats.mgmtFee, format: (v: number) => `${v.toFixed(2)}%`,   lowerIsBetter: true  },
                { label: 'Performance Fee',       myVal: fund.performance_fee,         peer: peerStats.perfFee, format: (v: number) => `${v.toFixed(2)}%`,   lowerIsBetter: true  },
                { label: 'Minimum Subscription', myVal: fund.minimum_subscription,    peer: peerStats.minSub,  format: (v: number) => `$${v.toFixed(1)}mm`, lowerIsBetter: true  },
                { label: 'AUM (Midpoint)',        myVal: (fund.aum_min + fund.aum_max) / 2, peer: peerStats.aum, format: (v: number) => `$${v.toFixed(0)}mm`, lowerIsBetter: false },
              ].map((row, i) => {
                const q = getQuartile(row.myVal, row.peer.min, row.peer.max, row.lowerIsBetter);
                const diff = row.myVal - row.peer.avg;
                return (
                  <div key={i} style={card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '4px' }}>{row.label}</div>
                        <div style={{ fontSize: '24px', fontFamily: 'Georgia, serif', color: C.navy, fontWeight: 400 }}>{row.format(row.myVal)}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 10px', borderRadius: '2px', background: q.color + '15', color: q.color, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                          {q.label}
                        </span>
                        <div style={{ fontSize: '11px', color: C.muted, marginTop: '6px' }}>
                          Peer avg: <span style={{ fontFamily: '"Courier New", monospace', fontWeight: 600 }}>{row.format(row.peer.avg)}</span>
                          <span style={{ color: diff <= 0 ? C.success : C.danger, marginLeft: '6px', fontFamily: '"Courier New", monospace', fontWeight: 600 }}>
                            ({diff >= 0 ? '+' : ''}{row.format(diff)})
                          </span>
                        </div>
                      </div>
                    </div>
                    <Bar value={row.myVal} min={row.peer.min} max={row.peer.max} color={q.color} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: C.mutedLt, marginTop: '2px', fontFamily: '"Courier New", monospace' }}>
                      <span>Min: {row.format(row.peer.min)}</span>
                      <span>Max: {row.format(row.peer.max)}</span>
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: '11px', color: C.mutedLt, textAlign: 'center', marginTop: '4px', letterSpacing: '0.2px' }}>
                Based on {peerStats.count} anonymous peer funds · All peer data aggregated only
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}